import { useRouter } from "next/router";
import { ParsedUrlQuery } from "querystring";
import React, {
  ChangeEvent,
  ReactElement,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { ServicePaidFlag, ServiceViewType, UserPlan } from "@/apis/clients";
import couponApi from "@/apis/coupon/couponApi";
import { ApiResponseError, ApiValidationError } from "@/apis/errors";
import { Button } from "@/components/atoms/buttons/Button";
import { FormCompanyItems } from "@/components/editors/template-blocks/service-form/form-items/FormCompanyItems";
import { TermPolicy } from "@/components/molecules/term-policy/TermPolicy";
import { AdvertisementFooter } from "@/components/organisms/footer/AdvertisementFooter";
import { BlockHeader } from "@/components/organisms/header/BlockHeader";
import {
  ServiceMenuData,
  ServiceMenuOptionDetail,
} from "@/components/organisms/service-form/ServiceMenu";
import ServiceMenuResponsive from "@/components/organisms/service-form/ServiceMenuResponsive";
import StepHeaderLayout from "@/components/templates/layout/StepHeader";
import useApiResponseErrorHandler from "@/hooks/useApiResponseErrorHandler";
import { useLoading } from "@/states/loading/operations";
import {
  useFetchServiceCalendarForm,
  useFetchServiceForm,
  useFetchServiceFormByService,
  useHandleFieldValueChange,
  useClearStalePaymentSelection,
  useSetIsAgreePolicy,
  useSetFormPayment,
} from "@/states/service-form/operations";
import {
  usePricingSummary,
  ConfirmServiceInfo,
  useCannotOrderReasons,
  useConfirmInputList,
  useIsAgreePolicy,
  useServiceFormUiState,
  useServiceInfo,
} from "@/states/service-form/selectors";
import {
  buildCouponOptionPayload,
  buildCouponContextKey,
  buildCouponTargetPayload,
} from "@/states/service-form/pricing";
import {
  buildCouponAppliedPayment,
  buildCouponClearedPayment,
  COUPON_AUTO_REMOVED_MESSAGE,
} from "@/states/service-form/coupon";
import {
  FieldValueChangeHandler,
  ServiceMenuDetail,
  ServiceOption,
} from "@/states/service-form/types";
import { isIframePage, scrollToElement } from "@/utils/common";
import { formatNumber } from "@/utils/number";

interface QueryParams {
  lpUuid?: string;
  simpleUuid?: string;
  iframeUuid?: string;
}

const PageForm = () => {
  const router = useRouter();
  const withLoading = useLoading();
  const { handlePageError } = useApiResponseErrorHandler();
  const serviceFormUiState = useServiceFormUiState();
  const onFieldValueChange = useHandleFieldValueChange();
  const fetchServiceForm = useFetchServiceForm();
  const fetchServiceCalendarForm = useFetchServiceCalendarForm();
  const fetchServiceFormByService = useFetchServiceFormByService();
  const cannotOrderReasons = useCannotOrderReasons();
  const service = useServiceInfo();
  const confirmInputList = useConfirmInputList();
  const pricingSummary = usePricingSummary();
  const setFormPayment = useSetFormPayment();
  const clearStalePaymentSelection = useClearStalePaymentSelection();
  const [hasActiveCoupon, setHasActiveCoupon] = useState(false);
  const [isCouponLoading, setIsCouponLoading] = useState(false);
  const [isCouponRevalidating, setIsCouponRevalidating] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [couponError, setCouponError] = useState("");
  const isCouponProcessing = isCouponLoading || isCouponRevalidating;
  const lastAppliedCouponOptionKeyRef = useRef<string | undefined>(undefined);
  const lastAppliedCouponEmailRef = useRef<string | undefined>(undefined);
  const latestCouponApplyRequestKeyRef = useRef<string | undefined>(undefined);
  const couponApplyRequestSeqRef = useRef(0);
  const couponAvailabilityRequestSeqRef = useRef(0);
  const couponRevalidationRequestSeqRef = useRef(0);
  const serviceFormUiStateRef = useRef(serviceFormUiState);

  useEffect(() => {
    serviceFormUiStateRef.current = serviceFormUiState;
  }, [serviceFormUiState]);

  const setAgreePolicy = useSetIsAgreePolicy();
  const isAgreePolicy = useIsAgreePolicy();

  // const isShowTerm = service?.viewType === ServiceViewType.BUTTON;
  const isShowTerm = false;

  const applyCouponClearedPayment = useCallback(
    (baseAmount: number) => {
      setFormPayment(
        buildCouponClearedPayment(
          baseAmount,
          serviceFormUiState.status === "Loaded"
            ? serviceFormUiState.payment.settlementMethod
            : undefined,
        ),
      );
      clearStalePaymentSelection(baseAmount);
    },
    [clearStalePaymentSelection, serviceFormUiState, setFormPayment],
  );

  const selectedCouponContext = useMemo(() => {
    if (serviceFormUiState.status !== "Loaded") {
      return null;
    }

    return buildCouponTargetPayload(
      serviceFormUiState.formData.service,
      serviceFormUiState.inputs.service,
    );
  }, [serviceFormUiState]);

  const selectedCouponOptionPayload = useMemo(() => {
    if (serviceFormUiState.status !== "Loaded") {
      return {};
    }

    return buildCouponOptionPayload(
      serviceFormUiState.formData.service,
      serviceFormUiState.inputs.service,
    );
  }, [serviceFormUiState]);

  const selectedCouponOptionKey = useMemo(
    () =>
      JSON.stringify({
        serviceOptionIds: [
          ...(selectedCouponOptionPayload.service_option_ids ?? []),
        ].sort((a, b) => a - b),
        serviceMenuOptionIds: [
          ...(selectedCouponOptionPayload.service_menu_option_ids ?? []),
        ].sort((a, b) => a - b),
      }),
    [selectedCouponOptionPayload],
  );

  const selectedCouponApplyRequestKey = useMemo(() => {
    if (serviceFormUiState.status !== "Loaded" || !selectedCouponContext) {
      return undefined;
    }

    const serviceInfo = serviceFormUiState.formData.service;
    if (
      serviceInfo.type !== "event-seminar" &&
      serviceInfo.type !== "form" &&
      serviceInfo.type !== "calendar"
    ) {
      return undefined;
    }

    return JSON.stringify({
      code: couponCode.trim(),
      supplierId: serviceInfo.supplierId,
      serviceId: serviceInfo.id,
      subtotal: pricingSummary.baseAmount,
      email: serviceFormUiState.inputs.customer.email || "",
      target: selectedCouponContext,
      options: selectedCouponOptionPayload,
    });
  }, [
    couponCode,
    pricingSummary.baseAmount,
    selectedCouponContext,
    selectedCouponOptionPayload,
    serviceFormUiState,
  ]);

  useLayoutEffect(() => {
    latestCouponApplyRequestKeyRef.current = selectedCouponApplyRequestKey;
  }, [selectedCouponApplyRequestKey]);

  const formUuid = useMemo(() => {
    const { uuid: formUuid } = router.query;
    if (formUuid === undefined || typeof formUuid !== "string") {
      return undefined;
    }
    return formUuid;
  }, [router.query]);

  const queryParams: QueryParams | undefined = useMemo(() => {
    if (!router.isReady) {
      return undefined;
    }

    const getLpId = (query: ParsedUrlQuery) => {
      const { lpId } = query;
      if (lpId === undefined || typeof lpId !== "string") {
        return undefined;
      }
      return lpId;
    };

    const getSimpleId = (query: ParsedUrlQuery) => {
      const { simpleId } = query;
      if (simpleId === undefined || typeof simpleId !== "string") {
        return undefined;
      }
      return simpleId;
    };

    const getIframeId = (query: ParsedUrlQuery) => {
      const { iframeId } = query;
      if (iframeId === undefined || typeof iframeId !== "string") {
        return undefined;
      }
      return iframeId;
    };

    return {
      lpUuid: getLpId(router.query),
      simpleUuid: getSimpleId(router.query),
      iframeUuid: getIframeId(router.query),
    };
  }, [router.isReady, router.query]);

  const isPaymentFormFlg = useCallback(() => {
    if (!service || serviceFormUiState.status !== "Loaded") {
      return false;
    }

    if (service.type === "calendar") {
      return (
        !!serviceFormUiState.inputs.service.serviceMenuReservationDateTime &&
        pricingSummary.finalAmount > 0
      );
    }
    if (service.type === "form" || service.type === "event-seminar") {
      return pricingSummary.finalAmount > 0;
    }

    if (
      service.type === "content-webinar" &&
      service.paid_flag === ServicePaidFlag.PAID
    ) {
      return pricingSummary.finalAmount > 0;
    }

    return false;
  }, [pricingSummary.finalAmount, service, serviceFormUiState]);

  const goBackPath = useMemo(() => {
    if (!queryParams) {
      return undefined;
    }

    if (queryParams.lpUuid) {
      return `/lp/${queryParams.lpUuid}`;
    }

    if (queryParams.simpleUuid) {
      return `/simple/${queryParams.simpleUuid}`;
    }

    if (queryParams.iframeUuid) {
      return `/iframe/${queryParams.iframeUuid}`;
    }

    if (formUuid && isIframePage(formUuid)) {
      return `/iframe/${formUuid}`;
    }

    if (formUuid) {
      return `/lp/${formUuid}`;
    }

    return undefined;
  }, [queryParams]);
  const goBack = useCallback(() => {
    if (service?.type === "calendar") {
      if (serviceFormUiState.status == "Loaded") {
        if (serviceFormUiState.inputs.service.serviceMenuReservationDateTime) {
          void router.back();
          return;
        }
      }
    }
    if (goBackPath) {
      void router.push(goBackPath);
    }
  }, [goBackPath, router]);
  const [serviceDetail, setServiceDetail] = React.useState<
    ConfirmServiceInfo["details"] | ServiceMenuDetail[]
  >([]);
  const [serviceOption, setServiceOption] = React.useState<
    ConfirmServiceInfo["options"] | ServiceOption[]
  >([]);

  const getOptions = (
    servicemenuOptionsSelectedId: number[],
    details: ServiceMenuDetail[] | undefined,
  ): ServiceOption[] => {
    if (!details || details.length == 0) return [];
    return details[0]?.options.filter((item) =>
      servicemenuOptionsSelectedId?.includes(item.id),
    );
  };

  useEffect(() => {
    if (serviceFormUiState.status === "Loaded" && service?.id) {
      const serviceMenuSelectedId =
        serviceFormUiState?.inputs?.service?.serviceMenuId;
      const servicemenuOptionsSelectedId =
        serviceFormUiState?.inputs?.service?.serviceMenuOptionIds;

      let details;
      if (service?.type === "calendar") {
        details = service?.menus?.filter(
          (item) => serviceMenuSelectedId === item.id,
        );
      } else if (service?.type === "event-seminar") {
        const ticketOrder = serviceFormUiState?.inputs?.service?.quantity;

        const detailsMenuSeminar =
          confirmInputList.service?.details?.map((detail) => ({
            ...detail,
            quantity: ticketOrder?.[detail.id] ?? 0,
          })) ?? [];

        details = [...detailsMenuSeminar];
      } else {
        details = confirmInputList.service.details;
      }

      const options: ServiceOption[] | ConfirmServiceInfo["options"] | [] =
        service?.type === "calendar"
          ? getOptions(
              servicemenuOptionsSelectedId,
              details as ServiceMenuDetail[],
            )
          : confirmInputList.service.options;
      if (
        (service.type === "event-seminar" ||
          service.type === "calendar" ||
          service.type === "form") &&
        details.length === 0
      ) {
        return goBack();
      }
      // service?.type === "calendar" ? details?.[0]?.options?.filter((item) => servicemenuOptionsSelectedId.includes(item.id))?.map((option: ServiceOption) => { return { ...option, price: option.amount } }) : confirmInputList.service.options;
      setServiceDetail(details);
      setServiceOption(options);
    }
  }, [
    serviceFormUiState,
    service,
    confirmInputList?.service?.details,
    confirmInputList?.service?.options,
    goBack,
  ]);
  useEffect(() => {
    if (pricingSummary.appliedCoupon?.couponCode) {
      setCouponCode(pricingSummary.appliedCoupon.couponCode);
    } else {
      setCouponCode("");
    }
  }, [pricingSummary.appliedCoupon?.couponCode]);

  const couponAvailabilityKey = useMemo(() => {
    if (serviceFormUiState.status !== "Loaded") {
      return null;
    }
    const serviceInfo = serviceFormUiState.formData.service;
    if (
      serviceInfo.type !== "event-seminar" &&
      serviceInfo.type !== "form" &&
      serviceInfo.type !== "calendar"
    ) {
      return null;
    }
    return JSON.stringify({
      supplierId: serviceInfo.supplierId,
      serviceId: serviceInfo.id,
      target: selectedCouponContext,
    });
  }, [selectedCouponContext, serviceFormUiState]);

  useEffect(() => {
    const requestSeq = ++couponAvailabilityRequestSeqRef.current;

    const fetchCouponAvailability = async () => {
      const currentState = serviceFormUiStateRef.current;
      if (currentState.status !== "Loaded" || !selectedCouponContext) {
        setHasActiveCoupon(false);
        return;
      }

      const serviceInfo = currentState.formData.service;
      if (
        serviceInfo.type !== "event-seminar" &&
        serviceInfo.type !== "form" &&
        serviceInfo.type !== "calendar"
      ) {
        setHasActiveCoupon(false);
        return;
      }

      try {
        const result = await couponApi.postCouponAvailability({
          couponAvailabilityRequest: {
            supplier_id: serviceInfo.supplierId,
            service_id: serviceInfo.id,
            ...selectedCouponContext,
          },
        });
        if (requestSeq === couponAvailabilityRequestSeqRef.current) {
          setHasActiveCoupon(result.has_active_coupon);
        }
      } catch (_error) {
        if (requestSeq === couponAvailabilityRequestSeqRef.current) {
          setHasActiveCoupon(false);
        }
      }
    };

    void fetchCouponAvailability();
    // serviceFormUiState is read via ref to avoid re-firing on unrelated
    // (e.g. text input) changes that cannot affect coupon availability.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couponAvailabilityKey]);

  useEffect(() => {
    if (serviceFormUiState.status !== "Loaded") {
      return;
    }

    const nextContextKey = buildCouponContextKey(
      serviceFormUiState.formData.service,
      serviceFormUiState.inputs.service,
    );

    if (
      serviceFormUiState.payment.couponContextKey &&
      serviceFormUiState.payment.couponContextKey !== nextContextKey
    ) {
      applyCouponClearedPayment(pricingSummary.baseAmount);
      lastAppliedCouponOptionKeyRef.current = undefined;
      lastAppliedCouponEmailRef.current = undefined;
      setCouponError(COUPON_AUTO_REMOVED_MESSAGE);
    }
  }, [applyCouponClearedPayment, pricingSummary.baseAmount, serviceFormUiState]);

  useEffect(() => {
    if (serviceFormUiState.status !== "Loaded") {
      couponRevalidationRequestSeqRef.current += 1;
      setIsCouponRevalidating(false);
      lastAppliedCouponOptionKeyRef.current = undefined;
      lastAppliedCouponEmailRef.current = undefined;
      return;
    }

    const appliedCouponCode = serviceFormUiState.payment.couponCode;
    if (!serviceFormUiState.payment.couponId || !appliedCouponCode) {
      couponRevalidationRequestSeqRef.current += 1;
      setIsCouponRevalidating(false);
      lastAppliedCouponOptionKeyRef.current = undefined;
      lastAppliedCouponEmailRef.current = undefined;
      return;
    }

    const nextContextKey = buildCouponContextKey(
      serviceFormUiState.formData.service,
      serviceFormUiState.inputs.service,
    );

    if (serviceFormUiState.payment.couponContextKey !== nextContextKey) {
      couponRevalidationRequestSeqRef.current += 1;
      setIsCouponRevalidating(false);
      return;
    }

    const currentCustomerEmail = serviceFormUiState.inputs.customer.email || "";

    if (lastAppliedCouponOptionKeyRef.current === undefined) {
      lastAppliedCouponOptionKeyRef.current = selectedCouponOptionKey;
      lastAppliedCouponEmailRef.current = currentCustomerEmail;
      return;
    }

    const isCouponOptionUnchanged =
      lastAppliedCouponOptionKeyRef.current === selectedCouponOptionKey;
    const isCouponEmailUnchanged =
      lastAppliedCouponEmailRef.current === currentCustomerEmail;

    if (isCouponOptionUnchanged && isCouponEmailUnchanged) {
      couponRevalidationRequestSeqRef.current += 1;
      setIsCouponRevalidating(false);
      return;
    }

    if (!selectedCouponContext) {
      return;
    }

    const requestSeq = ++couponRevalidationRequestSeqRef.current;
    const requestedOptionKey = selectedCouponOptionKey;
    const requestedEmail = currentCustomerEmail;

    const revalidateCoupon = async () => {
      setIsCouponRevalidating(true);
      setCouponError("");

      try {
        const serviceInfo = serviceFormUiState.formData.service;
        if (
          serviceInfo.type !== "event-seminar" &&
          serviceInfo.type !== "form" &&
          serviceInfo.type !== "calendar"
        ) {
          return;
        }

        const result = await couponApi.postCouponValidate({
          couponValidateRequest: {
            code: appliedCouponCode,
            supplier_id: serviceInfo.supplierId,
            service_id: serviceInfo.id,
            subtotal: pricingSummary.baseAmount,
            email: requestedEmail,
            ...selectedCouponContext,
            ...selectedCouponOptionPayload,
          },
        });

        if (requestSeq !== couponRevalidationRequestSeqRef.current) {
          return;
        }

        lastAppliedCouponOptionKeyRef.current = requestedOptionKey;
        lastAppliedCouponEmailRef.current = requestedEmail;
        setFormPayment(
          buildCouponAppliedPayment(
            result,
            serviceInfo,
            serviceFormUiState.inputs.service,
            serviceFormUiState.payment.settlementMethod,
          ),
        );
        setCouponCode(result.code);
      } catch (error) {
        if (requestSeq !== couponRevalidationRequestSeqRef.current) {
          return;
        }

        applyCouponClearedPayment(pricingSummary.baseAmount);
        lastAppliedCouponOptionKeyRef.current = undefined;
        lastAppliedCouponEmailRef.current = undefined;
        setCouponError(couponErrorToMessage(error));
      } finally {
        if (requestSeq === couponRevalidationRequestSeqRef.current) {
          setIsCouponRevalidating(false);
        }
      }
    };

    void revalidateCoupon();
  }, [
    applyCouponClearedPayment,
    pricingSummary.baseAmount,
    selectedCouponContext,
    selectedCouponOptionKey,
    selectedCouponOptionPayload,
    serviceFormUiState,
  ]);

  const couponErrorCodeToMessage = (code?: string) => {
    switch (code) {
      case "invalid_or_expired_code":
        return "入力したクーポンコードは有効ではありません。";
      case "usage_limit_exceeded":
        return "このクーポンはすでに利用上限に達しています。";
      case "coupon_not_applicable":
        return "選択中のメニューには適用できないクーポンです。";
      default:
        return "クーポンを適用できませんでした。";
    }
  };

  const couponErrorToMessage = (error: unknown) => {
    if (error instanceof ApiResponseError) {
      return couponErrorCodeToMessage(error.errors?.[0]?.code);
    }
    return "クーポンを適用できませんでした。";
  };

  const handleApplyCoupon = useCallback(async () => {
    if (serviceFormUiState.status !== "Loaded" || !selectedCouponContext) {
      return;
    }

    const serviceInfo = serviceFormUiState.formData.service;
    if (
      serviceInfo.type !== "event-seminar" &&
      serviceInfo.type !== "form" &&
      serviceInfo.type !== "calendar"
    ) {
      return;
    }

    const code = couponCode.trim();
    if (!code) {
      setCouponError("クーポンコードを入力してください。");
      return;
    }

    const requestKey = selectedCouponApplyRequestKey;
    if (!requestKey) {
      return;
    }
    const requestSeq = ++couponApplyRequestSeqRef.current;

    setIsCouponLoading(true);
    couponRevalidationRequestSeqRef.current += 1;
    setIsCouponRevalidating(false);
    setCouponError("");

    try {
      const result = await couponApi.postCouponValidate({
        couponValidateRequest: {
          code,
          supplier_id: serviceInfo.supplierId,
          service_id: serviceInfo.id,
          subtotal: pricingSummary.baseAmount,
          email: serviceFormUiState.inputs.customer.email || "",
          ...selectedCouponContext,
          ...selectedCouponOptionPayload,
        },
      });

      if (
        requestSeq !== couponApplyRequestSeqRef.current ||
        requestKey !== latestCouponApplyRequestKeyRef.current
      ) {
        return;
      }

      lastAppliedCouponOptionKeyRef.current = selectedCouponOptionKey;
      lastAppliedCouponEmailRef.current =
        serviceFormUiState.inputs.customer.email || "";
      setFormPayment(
        buildCouponAppliedPayment(
          result,
          serviceInfo,
          serviceFormUiState.inputs.service,
          serviceFormUiState.payment.settlementMethod,
        ),
      );
      setCouponCode(result.code);
    } catch (error) {
      if (
        requestSeq !== couponApplyRequestSeqRef.current ||
        requestKey !== latestCouponApplyRequestKeyRef.current
      ) {
        return;
      }

      if (
        error instanceof ApiResponseError ||
        error instanceof ApiValidationError
      ) {
        setCouponError(couponErrorToMessage(error));
        return;
      }
      throw error;
    } finally {
      if (requestSeq === couponApplyRequestSeqRef.current) {
        setIsCouponLoading(false);
      }
    }
  }, [
    couponCode,
    pricingSummary.baseAmount,
    selectedCouponApplyRequestKey,
    selectedCouponContext,
    selectedCouponOptionKey,
    selectedCouponOptionPayload,
    serviceFormUiState,
    setFormPayment,
  ]);

  const handleCancelCoupon = useCallback(() => {
    setCouponError("");
    couponApplyRequestSeqRef.current += 1;
    setIsCouponLoading(false);
    couponRevalidationRequestSeqRef.current += 1;
    setIsCouponRevalidating(false);
    applyCouponClearedPayment(pricingSummary.baseAmount);
    lastAppliedCouponOptionKeyRef.current = undefined;
    lastAppliedCouponEmailRef.current = undefined;
    setCouponCode("");
  }, [applyCouponClearedPayment, pricingSummary.baseAmount]);

  useEffect(() => {
    if (formUuid === undefined || queryParams === undefined) {
      return;
    }
    void (async () => {
      await withLoading(async () => {
        try {
          if (queryParams.simpleUuid) {
            await fetchServiceCalendarForm(formUuid);
          } else if (formUuid && isIframePage(formUuid)) {
            await fetchServiceFormByService(+formUuid);
          } else {
            await fetchServiceForm(formUuid);
          }
        } catch (e) {
          if (e instanceof ApiResponseError) {
            await handlePageError(e);
          }

          throw e;
        }
      });
    })();
  }, [
    formUuid,
    queryParams,
    fetchServiceForm,
    fetchServiceCalendarForm,
    fetchServiceFormByService,
    handlePageError,
    withLoading,
  ]);
  let formItemsProps;

  const canSubmit = useMemo(() => {
    if (isCouponProcessing) {
      return false;
    }

    if (cannotOrderReasons.length > 0) {
      return false;
    }

    if (
      !serviceFormUiState ||
      serviceFormUiState.status !== "Loaded" ||
      !serviceFormUiState.errors
    ) {
      return false;
    }

    if (
      serviceFormUiState.errors.service.detailIds &&
      serviceFormUiState.errors.service.detailIds.length > 0
    ) {
      return false;
    }

    if (
      serviceFormUiState.errors.service.optionIds &&
      serviceFormUiState.errors.service.optionIds.length > 0
    ) {
      return false;
    }

    return Object.values(serviceFormUiState.errors.customer).every(
      (fieldErrors) => fieldErrors.length === 0,
    );
  }, [cannotOrderReasons.length, isCouponProcessing, serviceFormUiState]);
  const handleSubmit = useCallback(() => {
    if (!canSubmit) {
      const errorElements = document.getElementsByClassName("form-error-text");
      if (errorElements && errorElements[0]) {
        scrollToElement(errorElements[0], "center");
      }
      window.dispatchEvent(new Event("button.handleFormSubmit"));
      return;
    }
    if (formUuid === undefined || queryParams === undefined) {
      return;
    }

    let query = "";
    if (queryParams.lpUuid) {
      query = `lpId=${queryParams.lpUuid}&from=${ServiceViewType.BUTTON}`;
    } else if (queryParams.simpleUuid) {
      query = `simpleId=${queryParams.simpleUuid}&from=${ServiceViewType.BUTTON}`;
    } else if (queryParams.iframeUuid) {
      query = `iframeId=${queryParams.iframeUuid}&from=${ServiceViewType.BUTTON}`;
    } else {
      query = `from=${ServiceViewType.BUTTON}`;
    }
    if (isPaymentFormFlg()) {
      void router.push(`/form/${formUuid}/payment?${query}`);
      return;
    }
    void router.push(`/form/${formUuid}/confirm?${query}`);
  }, [router, formUuid, queryParams, canSubmit, isPaymentFormFlg]);

  const handleCouponChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setCouponCode(event.target.value);
      setCouponError("");
    },
    [],
  );

  const serviceMenuCouponProps = useMemo(
    () => ({
      isCouponVisible: hasActiveCoupon,
      isCouponApplied: pricingSummary.isCouponApplied,
      couponCode,
      isCouponLoading: isCouponProcessing,
      couponErrorMessage: couponError,
      onCouponChange: handleCouponChange,
      onCouponApply: () => void handleApplyCoupon(),
      onCouponCancel: handleCancelCoupon,
    }),
    [
      couponCode,
      couponError,
      handleApplyCoupon,
      handleCancelCoupon,
      handleCouponChange,
      hasActiveCoupon,
      isCouponProcessing,
      pricingSummary.isCouponApplied,
    ],
  );
  const shouldShowPriceSummary =
    pricingSummary.baseAmount > 0 || pricingSummary.isCouponApplied;
  const summaryPrice =
    pricingSummary.finalAmount > 0
      ? `${formatNumber(pricingSummary.finalAmount)}円`
      : pricingSummary.isCouponApplied
      ? "0円"
      : "無料";

  if (serviceFormUiState.status === "Loaded") {
    if (
      service?.type === "event-seminar" &&
      serviceFormUiState.inputs.service.detailIds.length === 0
    ) {
      if (
        queryParams &&
        queryParams.lpUuid &&
        isIframePage(queryParams.lpUuid)
      ) {
        return `/iframe/${queryParams.lpUuid}`;
      }
      if (queryParams && queryParams.lpUuid) {
        return `/lp/${queryParams.lpUuid}`;
      }
    }
    formItemsProps = {
      formData: serviceFormUiState.formData.customer,
      inputs: serviceFormUiState.inputs.customer,
      errors: serviceFormUiState.errors,
      onFieldValueChange: onFieldValueChange as FieldValueChangeHandler,
      options: serviceFormUiState.options,
      isBorder: false,
    };
  }

  return (
    <article className="mx-auto max-w-[620px] md:w-[620px]">
      <div className="px-4 pt-8 md:pt-10">
        {/* <p className="mt-10 text-xs leading-loose text-center md:mt-[52px]">
        <span className="text-accent">※</span>項目は入力必須項目です。
        <br className="md:hidden" />
        お手数ですがご記入お願いいたします。
      </p> */}

        <BlockHeader
          title={service?.name || ""}
          className="block md:hidden mb-6"
        />
        {serviceFormUiState.status === "Loaded" && (
          <>
            {serviceDetail?.length > 0 && (
              <div className="mb-8 md:mb-[64px]">
                <ServiceMenuResponsive
                  type={service?.type}
                  isFee={shouldShowPriceSummary}
                  summaryPrice={summaryPrice}
                  menus={serviceDetail as ServiceMenuData[]}
                  options={serviceOption as ServiceMenuOptionDetail[]}
                  serviceReserveStatus={service?.reserveStatus}
                  subtotalPrice={`${formatNumber(pricingSummary.baseAmount)}円`}
                  couponDiscountPrice={`-${formatNumber(
                    pricingSummary.discountAmount,
                  )}円`}
                  couponLabel={pricingSummary.appliedCoupon?.couponLabel}
                  {...serviceMenuCouponProps}
                />
              </div>
            )}
            <h1 className="text-[20px] leading-[30px] md:text-[32px] md:leading-[36px] font-bold text-center mb-6 md:mb-10">
              お客様情報を入力してください
            </h1>
            {formItemsProps ? (
              <FormCompanyItems props={formItemsProps} />
            ) : null}
            {cannotOrderReasons.length > 0 && (
              <div className="form-error-text mb-6">
                {cannotOrderReasons.map((reason, index) => (
                  <p key={index} className="text-xl text-center text-accent">
                    {reason}
                  </p>
                ))}
              </div>
            )}
            {isShowTerm && !isPaymentFormFlg() && (
              <div className="mt-6">
                <TermPolicy
                  serviceId={confirmInputList?.service?.id ?? ""}
                  setIsAgree={setAgreePolicy}
                  isAgree={isAgreePolicy}
                  isShowTerm={!!service?.viewTermFlag}
                />
              </div>
            )}
            <div className="flex flex-col items-center gap-y-4 w-full md:w-[320px] mx-auto mt-4">
              <Button
                className="!w-full"
                size="xl"
                disabled={
                  isCouponProcessing ||
                  (isShowTerm && !isAgreePolicy && !isPaymentFormFlg())
                }
                onClick={handleSubmit}
                type="submit"
                label={
                  isPaymentFormFlg() ? "支払方法選択へ進む" : "確認画面へ進む"
                }
              />
              <Button
                className="!w-full"
                size="xl"
                color="primary-outline"
                onClick={goBack}
                label={"前の画面に戻る"}
              />
            </div>
          </>
        )}
      </div>
      {serviceFormUiState.status === "Loaded" &&
        serviceFormUiState?.plan === UserPlan.FREE && (
          <>
            <AdvertisementFooter />
          </>
        )}
    </article>
  );
};

export default PageForm;

PageForm.getLayout = function getLayout(children: ReactElement) {
  return (
    <StepHeaderLayout pageName="お客様情報の入力" classNames="bg-white">
      {children}
    </StepHeaderLayout>
  );
};
